import React, { Component } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl } from 'react-intl';
import { array, object } from 'prop-types';
import ym from 'react-yandex-metrika';
import { financesConstants } from '../../../constants/finances';
import {
  startSetPaymentOrders,
  startCreatePayments,
  startCancelPayment,
  clearError,
} from '@actions/finances';
import { startSetBalance } from '@actions/webmaster';
import { startSetWallets } from '../../../actions/wallets';
import { startSetSlaveWallets, startSetSlaveBalance } from '../../../actions/slaves';
import { iFMessage } from '../../../hoc/IntlComponents';
import { getSlaveId } from '../../Statistics/statisticsHelpers';
import { PaymentOrdersView } from '../index';
import WithAlert from '../../../hoc/WithAlert';

class PaymentOrders extends Component {
  static propTypes = {
    orders: object.isRequired,
    wallets: array,
  };

  static defaultProps = {
    wallets: [],
    orders: { items: [], count: 0 },
    showAlertPayment: false,
    setShowAlertPayment: () => {},
  };

  constructor(props) {
    super(props);

    this.state = {
      loading: false,
      limit: 20,
      page: 0,
      currency: null,
      sessionId: 0,
      validTill: 0,
      error: null,
      status: null,
    };

    this.$el = React.createRef();
  }

  componentDidMount() {
    this.getPayments();
    this.getWallets();
  }

  componentDidUpdate(prevProps) {
    const { error, showAlert, clearError } = this.props;

    if (error !== prevProps.error && error) {
      showAlert({ alertType: 'danger', message: error.message, closeInSeconds: 5 });
      setTimeout(clearError, 5000);
    }
  }

  changePage = (page) => {
    this.setState(
      () => ({ page }),
      () => {
        this.getPayments();
      },
    );
  };

  getPayments = () => {
    const { isMember, startSetPaymentOrders } = this.props;
    const { limit, page } = this.state;
    const params = { limit, page };
    if (isMember) {
      params.id = getSlaveId();
    }
    startSetPaymentOrders(params, isMember);
  };

  getWallets = () => {
    const { isMember, startSetSlaveWallets, startSetWallets, startSetSlaveBalance } = this.props;
    !isMember ? startSetWallets() : startSetSlaveWallets(getSlaveId());
    if (!isMember) {
      startSetWallets();
    } else {
      const slaveId = getSlaveId();
      startSetSlaveWallets(slaveId);
      startSetSlaveBalance(slaveId);
    }
  };

  /**
   * @param {object} purse
   * @param {string} currency
   * @return {boolean}
   */
  isCurrencyAvailable = (purse, currency) =>
    purse.currency === currency || (purse.convert_from && purse.convert_from.includes(currency));

  getPurses = (wallets = []) => {
    const purses = wallets.reduce((purses, purse = {}) => {
      if (Array.isArray(purse.wallets) && purse.wallets.length > 0) {
        return [
          ...purses,
          ...purse.wallets.map((wallet) => {
            wallet.title = purse.title_print.replace('%s', wallet.number);
            wallet.messageAlert = purse.messageAlert;

            return wallet;
          }),
        ];
      }

      return purses;
    }, []);

    const { currency } = this.state;

    if (currency) {
      return purses.filter((purse) => this.isCurrencyAvailable(purse, currency));
    } else {
      return purses;
    }
  };

  getPurseType = (type) => {
    return this.getPropsWallets().find((purse) => purse.type === type);
  };

  getOrders = (orders = []) =>
    orders.reduce((orders, order) => {
      if (order) {
        const purse = this.getPurseType(order.method);
        return [...orders, { ...order, purseTitle: purse ? purse.title_print : '' }];
      }

      return orders;
    }, []);

  getRules = (wallets = []) =>
    wallets.reduce((rules, wallet) => {
      const {
        type,
        chunksAllowed,
        commission,
        fixedCommission,
        commissionText,
        maxPayment,
        maxPaymentDay,
        maxPaymentMonth,
        minPayment,
        sumRules,
        totalRules,
        showAlerts,
        showAlert,
        wallets,
      } = wallet;
      return [
        ...rules,
        {
          type,
          chunksAllowed,
          commission,
          fixedCommission,
          commissionText,
          maxPayment,
          maxPaymentDay,
          maxPaymentMonth,
          minPayment,
          sumRules,
          totalRules,
          showAlerts,
          showAlert,
          wallets,
        },
      ];
    }, []);

  handleSubmit = (data) => {
    const { intl, isMember, startCreatePayments, showAlert } = this.props;
    this.setState(() => ({ loading: true }));

    const params = { ...data };

    if (isMember) {
      params.id = getSlaveId();
    }

    return startCreatePayments(params, isMember).then((response) => {
      if (response.type === financesConstants.ADD_PAYMENTS_SUCCESS) {
        ym('reachGoal', 'Payout');
        showAlert({
          alertType: 'success',
          message: iFMessage({ intl, id: 'payments.payment.success' }),
        });
        this.setState(() => ({
          loading: false,
          sessionId: 0,
          validTill: 0,
          error: null,
          status: null,
        }));
        this.getPayments();
        this.props.startSetBalance();
      } else {
        if (response.status === 401) {
          const { verification } = response.data.response;
          const { sessionId, validTill, type } = verification;

          const transport = verification.transport ? verification.transport : this.state.transport;

          this.setState(() => ({
            sessionId,
            validTill,
            type,
            status: response.status,
            error: null,
            loading: false,
            transport,
          }));
        } else if (response.status === 400) {
          showAlert({ alertType: 'danger', message: response.error });
          this.setState(() => ({
            status: response.status,
            error: response.error,
            loading: false,
          }));
        } else if (response.status === 403) {
          this.setState(() => ({
            sessionId: 0,
            validTill: 0,
            error: null,
            status: null,
            loading: false,
          }));
          showAlert({ alertType: 'danger', message: response.error });
        } else {
          showAlert({ alertType: 'danger', message: response.error });
        }

        return { error: response.error, status: response.status };
      }
    });
  };

  currencyChangeHandler = (currency) => {
    this.setState(() => ({
      currency,
    }));
  };

  inSlavesPages = () => {
    return window.location.pathname.indexOf('/master/main/') >= 0;
  };

  getPropsWallets = () => {
    const {
      isMember,
      wallets: webmasterWallets,
      slaves: { wallets },
    } = this.props;
    if (!isMember && !this.inSlavesPages()) {
      return webmasterWallets;
    }
    return wallets;
  };

  getPropsBalance = () => {
    const {
      isMember,
      balance: webmasterBalance,
      slaves: { balance },
    } = this.props;
    if (!isMember && !this.inSlavesPages()) {
      return webmasterBalance;
    }
    return balance;
  };

  getPropsOrders = () => {
    const {
      isMember,
      orders: webmasterOrders,
      slaves: { paymentOrders },
    } = this.props;

    let orders = paymentOrders.items;
    let count = paymentOrders.count;
    if (!isMember && !this.inSlavesPages()) {
      orders = webmasterOrders.items;
      count = webmasterOrders.count;
    }
    return {
      orders: this.getOrders(orders),
      count,
    };
  };

  resetFormState = () =>
    this.setState({
      loading: false,
      sessionId: 0,
      validTill: 0,
      error: null,
      status: null,
    });

  handleCancelPayment = (id) => {
    const { startCancelPayment, showAlert, intl, startSetBalance } = this.props;

    startCancelPayment(id).then(() => {
      if (!this.props.error) {
        showAlert({
          alertType: 'success',
          message: intl.formatMessage({
            id: 'payments.cancel-payment.success',
            defaultMessage: 'Payment successfully cancelled',
          }),
          closeInSeconds: 5,
        });
        this.getPayments();
        startSetBalance();
      }
    });
  };

  render() {
    const { loading, limit, page, sessionId, validTill, error, status } = this.state;
    const wallets = this.getPropsWallets();
    const balance = this.getPropsBalance();
    const { orders, count } = this.getPropsOrders();
    const { showAlertPayment, setShowAlertPayment, setMessageAlert, messageAlert, showAlert } =
      this.props;
    const pageCount = Math.ceil(count / limit);
    const purses = this.getPurses(wallets);
    const rules = this.getRules(wallets);

    const data = {
      ...this.props,
      ...this.state,
      wallets,
      balance,
      orders,
      count,
      pageCount,
      timeDiff: validTill,
      purses,
      rules,
    };
    return (
      <PaymentOrdersView
        data={data}
        showAlert={showAlert}
        showAlertPayment={showAlertPayment}
        setMessageAlert={setMessageAlert}
        messageAlert={messageAlert}
        setShowAlertPayment={setShowAlertPayment}
        resetFormState={this.resetFormState}
        onSubmit={this.handleSubmit}
        onCurrencyChange={this.currencyChangeHandler}
        onPageChange={this.changePage}
        onCancelPaymentClick={this.handleCancelPayment}
      />
    );
  }
}

const mapStateToProps = (state) => ({
  orders: state.finances.paymentOrders,
  wallets: state.webmaster.wallets,
  currencies: state.app.globalSettings.currencies,
  balance: state.webmaster.balance,
  // добавить валюты юзеров
  slaves: state.slaves,
  cancelRequestStatus: state.finances.cancelRequestStatus,
  error: state.finances.error,
});

const mapDispatchToProps = (dispatch) => ({
  startSetPaymentOrders: (params, isMember) => dispatch(startSetPaymentOrders(params, isMember)),
  startCreatePayments: (params, isMember) => dispatch(startCreatePayments(params, isMember)),
  startSetWallets: () => dispatch(startSetWallets()),
  startSetSlaveWallets: (id) => dispatch(startSetSlaveWallets(id)),
  startSetSlaveBalance: (id) => dispatch(startSetSlaveBalance(id)),
  startCancelPayment: (id) => dispatch(startCancelPayment(id)),
  clearError: () => dispatch(clearError()),
  startSetBalance: () => dispatch(startSetBalance()),
});

export default connect(mapStateToProps, mapDispatchToProps)(WithAlert(injectIntl(PaymentOrders)));
